import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { InspectionFormSheet } from '../components/inspections/InspectionFormSheet';
import { InspectionDetailSheet } from '../components/inspections/InspectionDetailSheet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, MagnifyingGlass, PencilSimple, Trash, Eye, CalendarBlank,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  INSPECTION_TYPES, INSPECTION_TYPE_MAP,
  INSPECTION_STATUTS, INSPECTION_STATUT_MAP,
} from '../constants/inspectionTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  chantier_id: '', type: 'programmee', date: new Date().toISOString().slice(0, 10),
  titre: '', coordonnateur: '', statut: 'en_cours',
  notes_texte: '', synthese: '', observations: '',
  entreprises_concernees: [],
};

export function InspectionsPage() {
  const [inspections, setInspections] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChantier, setFilterChantier] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  const [detailId, setDetailId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [iRes, cRes] = await Promise.all([
        axios.get(`${API}/inspections`, { withCredentials: true }),
        axios.get(`${API}/chantiers?type=csps`, { withCredentials: true }),
      ]);
      setInspections(iRes.data || []);
      setChantiers((cRes.data || []).filter(c => c && c.id && c.name));
    } catch (_err) {
      setInspections([]); setChantiers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setIsFormOpen(true);
  };

  const openEdit = (insp) => {
    setEditing(insp);
    setFormData({
      chantier_id: insp.chantier_id || '',
      type: insp.type || 'programmee',
      date: insp.date || '',
      titre: insp.titre || '',
      coordonnateur: insp.coordonnateur || '',
      statut: insp.statut || 'en_cours',
      notes_texte: insp.notes_texte || '',
      synthese: insp.synthese || '',
      observations: insp.observations || '',
      entreprises_concernees: insp.entreprises_concernees || [],
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.chantier_id || !formData.type || !formData.date) {
      toast.error('Chantier, type et date sont requis');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/inspections/${editing.id}`, formData, { withCredentials: true });
        toast.success('Inspection mise a jour');
      } else {
        await axios.post(`${API}/inspections`, formData, { withCredentials: true });
        toast.success('Inspection creee');
      }
      setIsFormOpen(false);
      setEditing(null);
      fetchData();
    } catch (_err) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/inspections/${id}`, { withCredentials: true });
      toast.success('Inspection supprimee');
      setDeleteConfirm(null);
      fetchData();
    } catch (_err) { toast.error('Erreur suppression'); }
  };

  // Filtered list
  const filtered = inspections.filter(i => {
    if (filterChantier !== 'all' && i.chantier_id !== filterChantier) return false;
    if (filterType !== 'all' && i.type !== filterType) return false;
    if (filterStatut !== 'all' && i.statut !== filterStatut) return false;
    const q = searchTerm.toLowerCase().trim();
    if (q) {
      const hay = `${i.titre || ''} ${i.coordonnateur || ''} ${(i.entreprises_concernees || []).join(' ')} ${i.notes_texte || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const chantierName = (cid) => chantiers.find(c => c.id === cid)?.name || '-';

  return (
    <AppShell>
      <div className="space-y-6 p-4 sm:p-6" data-testid="inspections-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">Inspections CSPS</h1>
            <p className="text-sm text-gray-500 mt-1">
              Module dedie - le Journal de chantier ne recoit qu'une ligne de tracabilite.
            </p>
          </div>
          <Button onClick={openCreate}
            className="bg-[#FF6B00] hover:bg-orange-600 h-12"
            data-testid="new-inspection-btn">
            <Plus size={18} className="mr-2" weight="bold" />
            Nouvelle inspection
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-11"
                data-testid="inspection-search"
              />
            </div>
            <Select value={filterChantier} onValueChange={setFilterChantier}>
              <SelectTrigger className="h-11" data-testid="filter-chantier">
                <SelectValue placeholder="Chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="all">Tous les chantiers</SelectItem>
                {chantiers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-11" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="all">Tous les types</SelectItem>
                {INSPECTION_TYPES.map(t => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="h-11" data-testid="filter-statut">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="all">Tous les statuts</SelectItem>
                {INSPECTION_STATUTS.map(s => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="py-20 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00] mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <Card data-testid="inspections-empty">
            <CardContent className="py-16 text-center">
              <CalendarBlank size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">Aucune inspection pour le moment</p>
              <Button onClick={openCreate} className="bg-[#FF6B00] hover:bg-orange-600">
                Creer la premiere inspection
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="inspections-list">
            {filtered.map(i => {
              const typeInfo = INSPECTION_TYPE_MAP[i.type];
              const statutInfo = INSPECTION_STATUT_MAP[i.statut];
              return (
                <Card key={i.id} className="hover:shadow-md transition cursor-pointer"
                  data-testid={`inspection-card-${i.id}`}
                  onClick={() => setDetailId(i.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {typeInfo && (
                          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                        )}
                        {statutInfo && (
                          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${statutInfo.color}`}>
                            {statutInfo.label}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); setDetailId(i.id); }}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          data-testid={`inspection-view-${i.id}`}>
                          <Eye size={16} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); openEdit(i); }}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          data-testid={`inspection-edit-${i.id}`}>
                          <PencilSimple size={16} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(i); }}
                          className="p-1.5 hover:bg-red-50 rounded text-red-500"
                          data-testid={`inspection-delete-${i.id}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-base font-semibold text-[#111827] truncate">
                      {i.titre || i.type_label || 'Inspection'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {chantierName(i.chantier_id)} - {i.date || '-'}
                    </p>
                    {i.coordonnateur && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Coordonnateur : <span className="text-gray-700">{i.coordonnateur}</span>
                      </p>
                    )}
                    {(i.entreprises_concernees || []).length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        Entreprises : <span className="text-gray-700">{i.entreprises_concernees.join(', ')}</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Sheet */}
      <InspectionFormSheet
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        formData={formData}
        setFormData={setFormData}
        chantiers={chantiers}
        onSubmit={handleSubmit}
        submitting={submitting}
        isEditing={!!editing}
      />

      {/* Detail Sheet */}
      <InspectionDetailSheet
        open={!!detailId}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
        inspectionId={detailId}
        chantiers={chantiers}
        onChange={fetchData}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => { if (!v) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="inspection-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette inspection ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irreversible. Les notes vocales et photos associees seront
              detachees mais non supprimees du stockage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inspection-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm.id)}
              className="bg-red-500 hover:bg-red-600"
              data-testid="inspection-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

export default InspectionsPage;
